/**
 * Best-effort decode for optional encoded deal payloads (base64 JSON or raw JSON string).
 * Returns null on any failure. Never throws.
 */

import { sanitizeNumericHotelIdForImageApi } from "./orderHotelImageService.js";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isLikelyUuid(s) {
  return typeof s === "string" && UUID_RE.test(s.trim());
}

/** LDS deal reference numbers (not base64 JSON) — must not be passed through atob. */
function isLikelyNumericDealRef(s) {
  const t = typeof s === "string" ? s.trim() : String(s ?? "").trim();
  return /^\d{8,}$/.test(t);
}

function pickHotelBlob(obj) {
  if (!obj || typeof obj !== "object") return null;
  if (obj.hotel && typeof obj.hotel === "object") return obj.hotel;
  if (obj.selectedHotel && typeof obj.selectedHotel === "object") return obj.selectedHotel;
  if (obj.fullDeal?.hotel && typeof obj.fullDeal.hotel === "object") return obj.fullDeal.hotel;
  if (obj.deal?.hotel && typeof obj.deal.hotel === "object") return obj.deal.hotel;
  return null;
}

/** Keys that commonly carry a numeric property id for /getImage (exclude dealId/orderId/roomId). */
const HOTEL_ID_KEY_RE =
  /^(HOTEL_ID|hotel[_]?id|hotelID|HotelId|gimHotelId|sabreHotelId|propertyId|globalPropertyId|hrHotelId|establishmentId|vendorHotelId|giataHotelId|tvlHotelId)$/i;

/**
 * @param {unknown} node
 * @param {number} depth
 * @param {WeakSet<object>} seen
 * @returns {string | number | null}
 */
function findNumericHotelIdDeep(node, depth, seen) {
  if (depth <= 0 || node == null) return null;
  if (typeof node !== "object") return null;
  if (seen.has(/** @type {object} */ (node))) return null;
  seen.add(/** @type {object} */ (node));

  if (Array.isArray(node)) {
    for (const item of node) {
      const hit = findNumericHotelIdDeep(item, depth - 1, seen);
      if (hit != null) return hit;
    }
    return null;
  }

  const o = /** @type {Record<string, unknown>} */ (node);
  for (const [k, v] of Object.entries(o)) {
    if (!HOTEL_ID_KEY_RE.test(k)) continue;
    if (sanitizeNumericHotelIdForImageApi(v) != null) return /** @type {string | number} */ (v);
  }
  for (const v of Object.values(o)) {
    const hit = findNumericHotelIdDeep(v, depth - 1, seen);
    if (hit != null) return hit;
  }
  return null;
}

/**
 * Scan persisted LDS JSON (e.g. full `order_response`) for a numeric hotel id when deal payloads
 * do not decode but the tree still contains HOTEL_ID / hotelId / gimHotelId, etc.
 * @param {unknown} obj
 * @returns {number | null}
 */
export function extractNumericHotelIdDeepFromJson(obj) {
  if (!obj || typeof obj !== "object") return null;
  const raw = findNumericHotelIdDeep(obj, 10, new WeakSet());
  return sanitizeNumericHotelIdForImageApi(raw);
}

/**
 * Only values that pass /getImage numeric rules. Generic `id` fields are tried last so a UUID
 * on hotel.id does not block findNumericHotelIdDeep from finding a real Gimmonix id elsewhere.
 * @returns {number | null}
 */
function pickNumericHotelIdForImageApi(hotel, room, obj) {
  const h = hotel && typeof hotel === "object" ? hotel : null;
  const r = room && typeof room === "object" ? room : null;
  const o = obj && typeof obj === "object" ? obj : null;
  const shallow = [
    h?.hotelId,
    h?.hotelID,
    h?.HotelId,
    h?.hotelid,
    h?.HOTEL_ID,
    r?.hotelId,
    r?.hotelid,
    r?.hotel_id,
    o?.hotelId,
    o?.hotelID,
    o?.HotelId,
    o?.hotelid,
    o?.hotel_id,
    o?.HOTEL_ID,
    o?.gimHotelId,
    o?.sabreHotelId,
    h?.id,
    o?.id,
  ];
  for (const c of shallow) {
    const n = sanitizeNumericHotelIdForImageApi(c);
    if (n != null) return n;
  }
  const deepRaw = findNumericHotelIdDeep(o ?? {}, 6, new WeakSet());
  return sanitizeNumericHotelIdForImageApi(deepRaw);
}

function normalizeDecoded(obj) {
  if (!obj || typeof obj !== "object") return null;
  const hotel = pickHotelBlob(obj) || obj;
  const room =
    (Array.isArray(obj.rooms) && obj.rooms[0]) ||
    (Array.isArray(obj.roomSet) && obj.roomSet[0]) ||
    (Array.isArray(obj.selectedRooms) && obj.selectedRooms[0]) ||
    null;

  const hotelName =
    pickStr(hotel?.hotelName, hotel?.hotelname, hotel?.name, hotel?.title, hotel?.fullname) ||
    pickStr(obj.hotelName, obj.hotel_name, obj.HotelName, obj.HOTEL_NAME);
  const hotelCity = pickStr(
    hotel?.hotelCity,
    hotel?.hotelcity,
    hotel?.city,
    hotel?.destination,
    obj.hotelCity,
    obj.city
  );
  const hotelBoard = pickStr(
    hotel?.board,
    hotel?.mealPlan,
    hotel?.boardBasis,
    obj.board,
    obj.hotelBoard
  );
  const hotelNights =
    typeof hotel?.nights === "number" && hotel.nights > 0
      ? hotel.nights
      : typeof obj.hotelNights === "number" && obj.hotelNights > 0
        ? obj.hotelNights
        : typeof obj.nights === "number" && obj.nights > 0
          ? obj.nights
          : null;
  const roomType = pickStr(
    room?.roomType,
    room?.room_type,
    room?.ROOM_TYPE,
    room?.name,
    obj.roomType,
    obj.room_type
  );
  const roomSet = Array.isArray(obj.roomSet) ? obj.roomSet : Array.isArray(obj.rooms) ? obj.rooms : null;
  const checkIn = pickStr(
    hotel?.checkInDate,
    hotel?.checkin,
    obj.checkIn,
    obj.check_in,
    obj.dep_date
  );
  const checkOut = pickStr(
    hotel?.checkOutDate,
    hotel?.checkout,
    obj.checkOut,
    obj.check_out,
    obj.ret_date
  );
  const hotelIdResolved = pickNumericHotelIdForImageApi(hotel, room, obj);
  const imageUrl =
    pickStr(hotel?.resourceUrl, hotel?.mainImage, hotel?.hotelMainImage, hotel?.hotelImage) ||
    (typeof hotel?.image === "string" ? hotel.image.trim() || null : null);

  if (
    !hotelName &&
    !hotelCity &&
    !hotelBoard &&
    hotelNights == null &&
    !roomType &&
    !checkIn &&
    !checkOut &&
    hotelIdResolved === null &&
    !imageUrl
  ) {
    return null;
  }

  return {
    hotelName: hotelName || "",
    hotelCity: hotelCity || "",
    hotelBoard: hotelBoard || "",
    hotelNights,
    roomType: roomType || "",
    roomSet,
    checkIn: checkIn || "",
    checkOut: checkOut || "",
    hotelId: hotelIdResolved,
    imageUrl: imageUrl || null,
  };
}

function pickStr(...xs) {
  for (const x of xs) {
    if (x == null) continue;
    const t = String(x).trim();
    if (t) return t;
  }
  return "";
}

/**
 * @param {unknown} encoded
 * @returns {null | ReturnType<typeof normalizeDecoded>}
 */
export function decodeLdsDealPayload(encoded) {
  if (encoded == null) return null;
  let s0 = String(encoded).trim();
  if (
    (s0.startsWith('"') && s0.endsWith('"')) ||
    (s0.startsWith("'") && s0.endsWith("'"))
  ) {
    s0 = s0.slice(1, -1).trim();
  }
  if (!s0 || isLikelyUuid(s0) || isLikelyNumericDealRef(s0)) return null;

  try {
    if (s0.startsWith("{") || s0.startsWith("[")) {
      const parsed = JSON.parse(s0);
      return normalizeDecoded(parsed);
    }
  } catch {
    /* ignore */
  }

  try {
    let b64 = s0.replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const bin = typeof atob === "function" ? atob(b64) : "";
    if (!bin) return null;
    const parsed = JSON.parse(bin);
    return normalizeDecoded(parsed);
  } catch {
    return null;
  }
}
